import React, { FC } from 'react'
import {
    BaobabFilterIcon,
    CerealFilterIcon,
    DapFilterIcon,
    PoultryFilterIcon,
    VegetableFilterIcon
} from 'assets/productListFilters/productListFilterIcons'
import { PRODUCT_CATEGORY } from 'helpers/constants'
import { colors } from 'styles/colors'

export type ProductCategoryFilterProps = {
    categories: string[]
    selectedCategory: string | null
    onSelectCategory: (category: string | null) => void
}

const ALL_CATEGORY = 'all'

const normalizeCategory = (category: string) => category.trim().toLowerCase()

/** Map API category labels onto known filter icons (e.g. Fertilizer → dap). */
const resolveFilterIconKey = (category: string): string => {
    const normalized = normalizeCategory(category)
    if (
        normalized === PRODUCT_CATEGORY.DAP ||
        normalized === 'fertilizer' ||
        normalized === 'fertiliser'
    ) {
        return PRODUCT_CATEGORY.DAP
    }
    return normalized
}

const iconColor = (isSelected: boolean) =>
    isSelected ? colors.primary : colors.mediumGrey

const renderCategoryIcon = (category: string, isSelected: boolean) => {
    const color = iconColor(isSelected)
    const iconKey = resolveFilterIconKey(category)
    const size = 28

    if (iconKey === ALL_CATEGORY) {
        return <BaobabFilterIcon width={size} height={size} color={color} />
    }

    if (iconKey === PRODUCT_CATEGORY.POULTRY) {
        return <PoultryFilterIcon width={size} height={size} color={color} />
    }

    if (iconKey === PRODUCT_CATEGORY.CEREAL) {
        return <CerealFilterIcon width={size} height={size} color={color} />
    }

    if (iconKey === PRODUCT_CATEGORY.VEGETABLE) {
        return <VegetableFilterIcon width={size} height={size} color={color} />
    }

    if (iconKey === PRODUCT_CATEGORY.DAP) {
        return <DapFilterIcon width={size} height={size} color={color} />
    }

    return (
        <span className="product-category-filter__fallback" aria-hidden="true">
            {category.trim().charAt(0).toUpperCase()}
        </span>
    )
}

const CATEGORY_CHIP_ORDER = [
    PRODUCT_CATEGORY.CEREAL,
    PRODUCT_CATEGORY.POULTRY,
    PRODUCT_CATEGORY.VEGETABLE,
    PRODUCT_CATEGORY.DAP
]

const sortCategoriesForDisplay = (categories: string[]) => {
    const remaining = [...categories]
    const ordered: string[] = []

    CATEGORY_CHIP_ORDER.forEach((key) => {
        const index = remaining.findIndex(
            (category) => resolveFilterIconKey(category) === key
        )
        if (index >= 0) {
            ordered.push(remaining[index])
            remaining.splice(index, 1)
        }
    })

    return [...ordered, ...remaining]
}

export const ProductCategoryFilter: FC<ProductCategoryFilterProps> = ({
    categories,
    selectedCategory,
    onSelectCategory
}) => {
    if (categories.length === 0) {
        return null
    }

    const displayCategories = sortCategoriesForDisplay(categories)
    const isAllSelected = selectedCategory === null

    return (
        <div
            className="product-category-filter"
            data-testid="product-category-filter"
        >
            <button
                type="button"
                aria-label="all categories"
                aria-pressed={isAllSelected}
                data-testid="product-category-chip-all"
                className={[
                    'product-category-filter__chip',
                    isAllSelected
                        ? 'product-category-filter__chip--selected'
                        : ''
                ]
                    .join(' ')
                    .trim()}
                onClick={() => onSelectCategory(null)}
            >
                {renderCategoryIcon(ALL_CATEGORY, isAllSelected)}
            </button>
            {displayCategories.map((category) => {
                const normalized = normalizeCategory(category)
                const isSelected =
                    selectedCategory !== null &&
                    normalizeCategory(selectedCategory) === normalized

                return (
                    <button
                        key={normalized}
                        type="button"
                        aria-label={category}
                        aria-pressed={isSelected}
                        data-testid={`product-category-chip-${normalized}`}
                        className={[
                            'product-category-filter__chip',
                            isSelected
                                ? 'product-category-filter__chip--selected'
                                : ''
                        ]
                            .join(' ')
                            .trim()}
                        onClick={() =>
                            onSelectCategory(isSelected ? null : category)
                        }
                    >
                        {renderCategoryIcon(category, isSelected)}
                    </button>
                )
            })}
        </div>
    )
}
